import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../auth/useAuth';
import { useManagedLocations } from '../auth/useManagedLocations';
import { LeaveAllocationTable } from './components/LeaveAllocationTable';
import { LeaveRequestList } from './components/LeaveRequestList';
import { LeaveRequestForm, LeaveRequestFormValues } from './components/LeaveRequestForm';
import { SelfLeaveRequestDialog } from './components/SelfLeaveRequestDialog';
import { StatusFilterSelect, StatusFilterValue } from './components/StatusFilterSelect';
import { useLeaveActions, useLeaveAllocations, useLeaveRequests } from './hooks';
import { LeaveRequestFilter, LeaveRequestRecord } from './types';

interface TabConfig {
  label: string;
  value: string;
  description: string;
}

type AppRole = 'USER' | 'MANAGER' | 'ADMIN';

const ROLE_TAB_MAP: Record<AppRole, TabConfig[]> = {
  ADMIN: [
    {
      label: 'Allocation Overview',
      value: 'allocations',
      description: 'Track leave requests that have been approved, denied, or remain pending across all staff.',
    },
    {
      label: 'Pending Requests',
      value: 'pending',
      description: 'Review the latest submissions and ensure nothing is blocked awaiting approval.',
    },
    {
      label: 'All Leave',
      value: 'all-leave',
      description: 'Browse every request with filters for status and year to help with audits.',
    },
  ],
  MANAGER: [
    {
      label: 'Team Leave',
      value: 'team',
      description: 'Monitor upcoming leave for your location so scheduling remains balanced.',
    },
    {
      label: 'Pending Approval',
      value: 'queue',
      description: 'Action the latest requests from your direct reports.',
    },
    {
      label: 'My Leave',
      value: 'self',
      description: 'Keep track of your own submissions and their status.',
    },
  ],
  USER: [
    {
      label: 'My Requests',
      value: 'requests',
      description: 'Review the status of your previously submitted leave requests.',
    },
  ],
};

const getRecentYears = (range = 5): number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: range }, (_, index) => currentYear - index);
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CHANGE_PENDING: 'Change requested',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  CANCELLED: 'Cancelled',
};

const timestampToDate = (value?: Timestamp | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as any)?.toDate === 'function') {
    return (value as any).toDate();
  }
  return null;
};

const LeaveManager: React.FC = () => {
  const { user, userRole, userMetadata } = useAuth();
  const assignedLocations = useManagedLocations();
  const effectiveRole: AppRole = (userRole as AppRole) ?? 'USER';
  const {
    approveLeaveRequest,
    denyLeaveRequest,
    cancelOwnRequest,
    requestChange,
  } = useLeaveActions();

  const availableTabs = useMemo<TabConfig[]>(() => ROLE_TAB_MAP[effectiveRole] || ROLE_TAB_MAP.USER, [effectiveRole]);
  const [activeTab, setActiveTab] = useState<string>(availableTabs[0]?.value ?? 'requests');

  useEffect(() => {
    if (!availableTabs.find((tab) => tab.value === activeTab)) {
      setActiveTab(availableTabs[0]?.value ?? 'requests');
    }
  }, [availableTabs, activeTab]);

	// Intentionally avoid global keyboard shortcuts here.
	// MUI Tabs already support keyboard navigation when the tablist has focus,
	// and global listeners tend to conflict with assistive tech and expected browser shortcuts.

  const [adminAllocationYear, setAdminAllocationYear] = useState<number>(new Date().getFullYear());
  const [adminAllYear, setAdminAllYear] = useState<number | 'ALL'>(new Date().getFullYear());
  const [adminAllStatus, setAdminAllStatus] = useState<StatusFilterValue>('ALL');

  const [managerTeamStatus, setManagerTeamStatus] = useState<StatusFilterValue>('ALL');
  const [managerTeamYear, setManagerTeamYear] = useState<number | 'ALL'>('ALL');

  const [selfStatusFilter, setSelfStatusFilter] = useState<StatusFilterValue>('ALL');
	/**
	 * Modal state for self-submitted leave requests.
	 * This is the primary request entry point for `USER` and also available for `MANAGER`.
	 */
	const [selfRequestDialogOpen, setSelfRequestDialogOpen] = useState(false);

  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    mode: 'APPROVE' | 'DENY';
    request: LeaveRequestRecord | null;
  }>({ open: false, mode: 'APPROVE', request: null });

  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    request: LeaveRequestRecord | null;
  }>({ open: false, request: null });

  const [changeDialog, setChangeDialog] = useState<{
    open: boolean;
    request: LeaveRequestRecord | null;
  }>({ open: false, request: null });

  const managerLocations = useMemo(
    () => (effectiveRole === 'MANAGER' ? assignedLocations : ([] as string[])),
    [effectiveRole, assignedLocations]
  );

  const activeTabMeta = availableTabs.find((tab) => tab.value === activeTab);

  const openApproveDialog = (request: LeaveRequestRecord) =>
    setDecisionDialog({ open: true, mode: 'APPROVE', request });
  const openDenyDialog = (request: LeaveRequestRecord) =>
    setDecisionDialog({ open: true, mode: 'DENY', request });
  const closeDecisionDialog = () => setDecisionDialog({ open: false, mode: 'APPROVE', request: null });

  const openCancelDialog = (request: LeaveRequestRecord) => setCancelDialog({ open: true, request });
  const closeCancelDialog = () => setCancelDialog({ open: false, request: null });

  const openChangeDialog = (request: LeaveRequestRecord) => setChangeDialog({ open: true, request });
  const closeChangeDialog = () => setChangeDialog({ open: false, request: null });

	const openSelfRequestDialog = () => {
		console.info('[leave-ui] open request leave dialog', { role: effectiveRole, userUid: user?.uid });
		setSelfRequestDialogOpen(true);
	};
	const closeSelfRequestDialog = () => {
		console.info('[leave-ui] close request leave dialog', { role: effectiveRole, userUid: user?.uid });
		setSelfRequestDialogOpen(false);
	};

  const handleDecisionSubmit = async (comment?: string) => {
    if (!decisionDialog.request) return;
    if (decisionDialog.mode === 'APPROVE') {
      await approveLeaveRequest({
        request: decisionDialog.request,
        managerComment: comment,
        status: 'APPROVED',
      });
    } else {
      await denyLeaveRequest({
        request: decisionDialog.request,
        managerComment: comment,
        status: 'DENIED',
      });
    }
    closeDecisionDialog();
  };

  const handleCancelSubmit = async () => {
    if (!cancelDialog.request) return;
    await cancelOwnRequest(cancelDialog.request);
    closeCancelDialog();
  };

  const handleChangeSubmit = async (values: LeaveRequestFormValues) => {
    if (!changeDialog.request || !values.startDate || !values.endDate) return;
    await requestChange(changeDialog.request, {
      startDate: values.startDate,
      endDate: values.endDate,
      reason: values.reason,
      comment: values.comment,
    });
    closeChangeDialog();
  };

  const renderTabContent = () => {
    switch (effectiveRole) {
      case 'ADMIN':
        if (activeTab === 'allocations') {
          return (
            <AdminAllocationTab
              year={adminAllocationYear}
              onYearChange={setAdminAllocationYear}
            />
          );
        }
        if (activeTab === 'pending') {
          return <AdminPendingTab onApprove={openApproveDialog} onDeny={openDenyDialog} />;
        }
        if (activeTab === 'all-leave') {
          return (
            <AdminAllLeaveTab
              statusFilter={adminAllStatus}
              onStatusChange={setAdminAllStatus}
              yearFilter={adminAllYear}
              onYearFilterChange={setAdminAllYear}
            />
          );
        }
        break;
      case 'MANAGER':
        if (activeTab === 'team') {
          return (
            <ManagerTeamLeaveTab
              managerLocations={managerLocations}
              statusFilter={managerTeamStatus}
              onStatusFilterChange={setManagerTeamStatus}
              yearFilter={managerTeamYear}
              onYearFilterChange={setManagerTeamYear}
            />
          );
        }
        if (activeTab === 'queue') {
          return (
            <ManagerPendingTab
              managerLocations={managerLocations}
              onApprove={openApproveDialog}
              onDeny={openDenyDialog}
            />
          );
        }
        if (activeTab === 'self') {
          return (
            <SelfLeaveTab
              userId={user?.uid || null}
              statusFilter={selfStatusFilter}
              onStatusFilterChange={setSelfStatusFilter}
              title="My requests"
              onCancel={openCancelDialog}
              onRequestChange={openChangeDialog}
            />
          );
        }
        break;
      case 'USER':
      default:
        if (activeTab === 'requests') {
          return (
            <SelfLeaveTab
              userId={user?.uid || null}
              statusFilter={selfStatusFilter}
              onStatusFilterChange={setSelfStatusFilter}
              title="My leave requests"
              onCancel={openCancelDialog}
              onRequestChange={openChangeDialog}
            />
          );
        }
        break;
    }
    return null;
  };

  if (!user) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="warning">You need to be signed in to manage leave requests.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Leave Manager
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Coordinate annual leave requests, approvals, and allocation oversight.
            </Typography>
          </Box>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
						{(effectiveRole === 'USER' || effectiveRole === 'MANAGER') && (
							<Button
								variant="contained"
								startIcon={<EditCalendarIcon />}
								onClick={openSelfRequestDialog}
							>
								Request leave
							</Button>
						)}

					</Stack>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
				{availableTabs.length > 1 && (
					<>
						<Tabs
							value={activeTab}
							onChange={(_, value) => setActiveTab(value)}
							variant="scrollable"
							scrollButtons="auto"
							sx={{ px: 2, pt: 2, borderBottom: '1px solid', borderColor: 'divider' }}
						>
							{availableTabs.map((tab) => (
								<Tab key={tab.value} value={tab.value} label={tab.label} sx={{ textTransform: 'none', fontWeight: 600 }} />
							))}
						</Tabs>
						<Divider />
					</>
				)}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {activeTabMeta && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {activeTabMeta.description}
            </Typography>
          )}
          {renderTabContent()}
        </Box>
      </Paper>

      <LeaveDecisionDialog
        open={decisionDialog.open}
        mode={decisionDialog.mode}
        onClose={closeDecisionDialog}
        onSubmit={handleDecisionSubmit}
      />

      <ConfirmCancelDialog
        open={cancelDialog.open}
        onClose={closeCancelDialog}
        onConfirm={handleCancelSubmit}
      />

      <ChangeRequestDialog
        open={changeDialog.open}
        request={changeDialog.request}
        onClose={closeChangeDialog}
        onSubmit={handleChangeSubmit}
      />

			{(effectiveRole === 'USER' || effectiveRole === 'MANAGER') && (
				<SelfLeaveRequestDialog
					open={selfRequestDialogOpen}
					onClose={closeSelfRequestDialog}
					staffName={userMetadata?.displayName || user?.displayName || 'Coach'}
					userUid={user?.uid || ''}
					onSubmitSuccess={() => {
						// Managers are usually working in team tabs; after self-submitting, bring them to "My Leave".
						if (effectiveRole === 'MANAGER') {
							setActiveTab('self');
						}
					}}
				/>
			)}
    </Box>
  );
};

interface AdminAllocationTabProps {
  year: number;
  onYearChange: (year: number) => void;
}

const AdminAllocationTab: React.FC<AdminAllocationTabProps> = ({ year, onYearChange }) => {
  const allocationFilter = useMemo(() => ({
    role: 'ADMIN' as const,
    year,
  }), [year]);

  const { data, loading, error } = useLeaveAllocations(allocationFilter);
  const yearOptions = useMemo(() => getRecentYears(5), []);

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    onYearChange(parseInt(event.target.value, 10));
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load allocation data right now.</Alert>}
      <LeaveAllocationTable
        allocations={data}
        loading={loading}
        headerSlot={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Allocation summary
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={String(year)} label="Year" onChange={handleYearChange}>
                {yearOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        }
      />
    </Stack>
  );
};

interface AdminPendingTabProps {
  onApprove: (request: LeaveRequestRecord) => void;
  onDeny: (request: LeaveRequestRecord) => void;
}

const AdminPendingTab: React.FC<AdminPendingTabProps> = ({ onApprove, onDeny }) => {
  const filter = useMemo<LeaveRequestFilter>(() => ({ role: 'ADMIN' }), []);
  const { data, loading, error } = useLeaveRequests(filter);
  // Client-side filter to include both PENDING and CHANGE_PENDING without Firestore "in" constraints.
  const pendingRequests = useMemo(
		() => data.filter((request) => request.status === 'PENDING' || request.status === 'CHANGE_PENDING'),
		[data]
	);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load pending requests.</Alert>}
      <LeaveRequestList
        title="Pending approval queue"
        requests={pendingRequests}
        loading={loading}
        showStaffColumn
        emptyState={<Typography>No pending requests right now - great job staying on top of approvals!</Typography>}
        renderActions={(request) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Approve request">
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                onClick={() => onApprove(request)}
              >
                Approve
              </Button>
            </Tooltip>
            <Tooltip title="Deny request">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<HighlightOffIcon fontSize="small" />}
                onClick={() => onDeny(request)}
              >
                Deny
              </Button>
            </Tooltip>
          </Stack>
        )}
      />
    </Stack>
  );
};

interface AdminAllLeaveTabProps {
  statusFilter: StatusFilterValue;
  onStatusChange: (value: StatusFilterValue) => void;
  yearFilter: number | 'ALL';
  onYearFilterChange: (value: number | 'ALL') => void;
}

const AdminAllLeaveTab: React.FC<AdminAllLeaveTabProps> = ({
  statusFilter,
  onStatusChange,
  yearFilter,
  onYearFilterChange,
}) => {
  const filter = useMemo<LeaveRequestFilter>(() => {
    const base: LeaveRequestFilter = { role: 'ADMIN' };
    if (statusFilter !== 'ALL') {
      base.status = statusFilter;
    }
    if (typeof yearFilter === 'number') {
      base.year = yearFilter;
    }
    return base;
  }, [statusFilter, yearFilter]);

  const { data, loading, error } = useLeaveRequests(filter);
  const yearOptions = useMemo(() => ['ALL', ...getRecentYears(5)] as Array<number | 'ALL'>, []);

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onYearFilterChange(value === 'ALL' ? 'ALL' : parseInt(value, 10));
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load leave history.</Alert>}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
        <StatusFilterSelect value={statusFilter} onChange={onStatusChange} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Year</InputLabel>
          <Select value={String(yearFilter)} label="Year" onChange={handleYearChange}>
            {yearOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'ALL' ? 'All years' : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <LeaveRequestList
        title="All requests"
        requests={data}
        loading={loading}
        showStaffColumn
      />
    </Stack>
  );
};

interface ManagerTeamLeaveTabProps {
  managerLocations: string[];
  statusFilter: StatusFilterValue;
  onStatusFilterChange: (value: StatusFilterValue) => void;
  yearFilter: number | 'ALL';
  onYearFilterChange: (value: number | 'ALL') => void;
}

const ManagerTeamLeaveTab: React.FC<ManagerTeamLeaveTabProps> = ({
  managerLocations,
  statusFilter,
  onStatusFilterChange,
  yearFilter,
  onYearFilterChange,
}) => {
  const filter = useMemo<LeaveRequestFilter | null>(() => {
    if (!managerLocations || managerLocations.length === 0) {
      return null;
    }
    const base: LeaveRequestFilter = {
      role: 'MANAGER',
      managerLocations,
    };
    if (statusFilter !== 'ALL') {
      base.status = statusFilter;
    }
    if (typeof yearFilter === 'number') {
      base.year = yearFilter;
    }
    return base;
  }, [managerLocations, statusFilter, yearFilter]);

  const { data, loading, error } = useLeaveRequests(filter);
  const yearOptions = useMemo(() => ['ALL', ...getRecentYears(4)] as Array<number | 'ALL'>, []);

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onYearFilterChange(value === 'ALL' ? 'ALL' : parseInt(value, 10));
  };

  if (!managerLocations || managerLocations.length === 0) {
    return <Alert severity="info">Assign a location to your profile to view team leave.</Alert>;
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load team leave data.</Alert>}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
        <StatusFilterSelect value={statusFilter} onChange={onStatusFilterChange} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Year</InputLabel>
          <Select value={String(yearFilter)} label="Year" onChange={handleYearChange}>
            {yearOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'ALL' ? 'All years' : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <LeaveRequestList
        title="Team leave overview"
        requests={data}
        loading={loading}
        showStaffColumn
      />
    </Stack>
  );
};

interface ManagerPendingTabProps {
  managerLocations: string[];
  onApprove: (request: LeaveRequestRecord) => void;
  onDeny: (request: LeaveRequestRecord) => void;
}

const ManagerPendingTab: React.FC<ManagerPendingTabProps> = ({ managerLocations, onApprove, onDeny }) => {
  const filter = useMemo<LeaveRequestFilter | null>(() => {
    if (!managerLocations || managerLocations.length === 0) {
      return null;
    }
    return {
      role: 'MANAGER',
      managerLocations,
    };
  }, [managerLocations]);

  const { data, loading, error } = useLeaveRequests(filter);
  // Client-side filter to include both PENDING and CHANGE_PENDING without Firestore "in" constraints.
  const pendingRequests = useMemo(
		() => data.filter((request) => request.status === 'PENDING' || request.status === 'CHANGE_PENDING'),
		[data]
	);

  if (!managerLocations || managerLocations.length === 0) {
    return <Alert severity="info">Assign a location to your profile to triage pending requests.</Alert>;
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load pending requests for your team.</Alert>}
      <LeaveRequestList
        title="Awaiting your approval"
        requests={pendingRequests}
        loading={loading}
        showStaffColumn
        emptyState={<Typography>No outstanding requests from your team.</Typography>}
        renderActions={(request) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Approve request">
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                onClick={() => onApprove(request)}
              >
                Approve
              </Button>
            </Tooltip>
            <Tooltip title="Deny request">
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<HighlightOffIcon fontSize="small" />}
                onClick={() => onDeny(request)}
              >
                Deny
              </Button>
            </Tooltip>
          </Stack>
        )}
      />
    </Stack>
  );
};

interface SelfLeaveTabProps {
  userId: string | null;
  statusFilter: StatusFilterValue;
  onStatusFilterChange: (value: StatusFilterValue) => void;
  title: string;
  onCancel?: (request: LeaveRequestRecord) => void;
  onRequestChange?: (request: LeaveRequestRecord) => void;
}

const SelfLeaveTab: React.FC<SelfLeaveTabProps> = ({
  userId,
  statusFilter,
  onStatusFilterChange,
  title,
  onCancel,
  onRequestChange,
}) => {
  const filter = useMemo<LeaveRequestFilter | null>(() => {
    if (!userId) return null;
    const base: LeaveRequestFilter = {
      role: 'USER',
      uid: userId,
    };
    if (statusFilter !== 'ALL') {
      base.status = statusFilter;
    }
    return base;
  }, [userId, statusFilter]);

  const { data, loading, error } = useLeaveRequests(filter);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    data.forEach((request) => {
      counts[request.status] = (counts[request.status] || 0) + 1;
    });
    return counts;
  }, [data]);

  const renderActions = (request: LeaveRequestRecord) => {
    const actions: React.ReactNode[] = [];
    const canCancel = (request.status === 'PENDING' || request.status === 'CHANGE_PENDING') && Boolean(onCancel);
    const canRequestChange =
      (request.status === 'APPROVED' || request.status === 'PENDING') && Boolean(onRequestChange);

    if (canCancel) {
      actions.push(
        <Button
          key="cancel"
          size="small"
          color="error"
          variant="text"
          startIcon={<CancelScheduleSendIcon fontSize="small" />}
          onClick={() => onCancel?.(request)}
        >
          Cancel
        </Button>
      );
    }

    if (canRequestChange) {
      actions.push(
        <Button
          key="change"
          size="small"
          variant="outlined"
          startIcon={<EditCalendarIcon fontSize="small" />}
          onClick={() => onRequestChange?.(request)}
        >
          Request change
        </Button>
      );
    }

    if (actions.length === 0) return null;

    return (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {actions.map((action) => action)}
      </Stack>
    );
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">Unable to load your leave history.</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <StatusFilterSelect value={statusFilter} onChange={onStatusFilterChange} />
        <Typography variant="body2" color="text.secondary">
          {renderStatusSummary(statusCounts)}
        </Typography>
      </Stack>
      <LeaveRequestList
        title={title}
        requests={data}
        loading={loading}
        emptyState={<Typography>You have not submitted any leave requests yet.</Typography>}
        renderActions={renderActions}
      />
    </Stack>
  );
};

interface LeaveDecisionDialogProps {
  open: boolean;
  mode: 'APPROVE' | 'DENY';
  onClose: () => void;
  onSubmit: (comment?: string) => void | Promise<void>;
}

const LeaveDecisionDialog: React.FC<LeaveDecisionDialogProps> = ({ open, mode, onClose, onSubmit }) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open) {
      setComment('');
    }
  }, [open]);

  const title = mode === 'APPROVE' ? 'Approve leave request' : 'Deny leave request';
  const description =
    mode === 'APPROVE'
      ? 'Optional: add a short note for the staff member before approving.'
      : 'Add any context for the staff member about why this request is being denied.';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <TextField
          label="Manager comment"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          fullWidth
          minRows={3}
          multiline
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={mode === 'APPROVE' ? 'success' : 'error'}
          onClick={async () => {
            await onSubmit(comment.trim() ? comment : undefined);
          }}
        >
          {mode === 'APPROVE' ? 'Approve request' : 'Deny request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ConfirmCancelDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

const ConfirmCancelDialog: React.FC<ConfirmCancelDialogProps> = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Cancel leave request</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to cancel this request? Your manager will be notified.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep request</Button>
        <Button
          variant="contained"
          color="error"
          onClick={async () => {
            await onConfirm();
          }}
        >
          Cancel request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ChangeRequestDialogProps {
  open: boolean;
  request: LeaveRequestRecord | null;
  onClose: () => void;
  onSubmit: (values: LeaveRequestFormValues) => void | Promise<void>;
}

const ChangeRequestDialog: React.FC<ChangeRequestDialogProps> = ({ open, request, onClose, onSubmit }) => {
  const initialValues = useMemo<LeaveRequestFormValues | undefined>(() => {
    if (!request) return undefined;
    const start = timestampToDate(request.period?.startDate) || null;
    const end = timestampToDate(request.period?.endDate) || null;
    return {
      startDate: start,
      endDate: end,
      reason: request.reason || '',
      comment: request.comments?.staff || '',
    };
  }, [request]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request changes to leave</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Update the requested dates and provide any additional context for your manager.
        </Typography>
        <LeaveRequestForm
          onSubmit={onSubmit}
          submitLabel="Submit change request"
          helperNotice="Submitting a change will notify your manager and create a new pending request."
          initialValues={initialValues}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const renderStatusSummary = (counts: Record<string, number>): string => {
  if (!counts || Object.keys(counts).length === 0) {
    return 'No requests recorded yet.';
  }

  const summaryParts = Object.entries(counts)
    .map(([status, count]) => `${count} ${STATUS_LABELS[status] ?? status.toLowerCase()}`)
    .join(' · ');

  return summaryParts;
};

export default LeaveManager;
